import React, { useState, useEffect, useCallback } from "react";
import { Button } from "./ui";
import { cn } from "../lib/utils";
import { TYPOGRAPHY, getDrawerBackdropClass, getDrawerPanelClass } from "../lib/design-system";
import { useProperty } from "../contexts/PropertyContext";
import {
  X,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  Wrench,
  User,
  Check,
  XCircle,
  Package,
  MapPin,
  Calendar,
  CreditCard,
  Star,
  Clock,
  AlertTriangle,
  Shield,
  Loader2,
  Home,
  Video,
} from "lucide-react";

import { aiCheckout } from "../services/ai-checkout.api";
import {
  getPaymentMethods,
  attachPaymentMethod,
  setDefaultPaymentMethod,
  deletePaymentMethod,
  type PaymentMethod as SavedCard,
} from "../services/payment-methods.api";
import api from "../services/api";
import { getBusinessRules } from "../services/businessRules.api";
import { GoogleAddressAutocomplete } from "./address/GoogleAddressAutocomplete";
import { VirtualConsultStep } from "./ai-fix/steps/VirtualConsultStep";

// ΓöÇΓöÇ Types ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ

interface CommonIssue {
  id: string;
  title: string;
}

interface RootCause {
  id: string;
  title: string;
}

interface DIYStep {
  id: number;
  title: string;
  description: string;
}

interface Part {
  id: string;
  name: string;
  description: string;
  price: number;
  qty: number;
}

interface Technician {
  id: string;
  name: string;
  avatar: string;
  rating: number;
  experience: string;
  baseFee: number;
}

interface TimeSlot {
  id: string;
  date: string;
  time: string;
  available: boolean;
  beforeParts?: boolean;
  dateObj?: Date;
}

type FlowStep =
  | "describe"
  | "rootCause"
  | "diy"
  | "parts"
  | "address"
  | "technician"
  | "payment"
  | "confirmation"
  | "virtual-consult";

const _STEP_LABELS: Record<FlowStep, string> = {
  describe: "Describe Issue",
  rootCause: "Analysis",
  diy: "Troubleshooting",
  parts: "Parts",
  address: "Address",
  technician: "Technician",
  payment: "Payment",
  confirmation: "Confirmed",
  "virtual-consult": "Virtual Consult",
};

// ΓöÇΓöÇ Props ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ
const MOCK_TECHNICIANS: Technician[] = [
  { id: "t1", name: 'Robert "Bob" Fixit', avatar: "\u{1F468}\u200D\u{1F527}", rating: 4.9, experience: "15 years", baseFee: 125 },
  { id: "t2", name: "Mike Hammer", avatar: "\u{1F9D1}\u200D\u{1F527}", rating: 4.8, experience: "5 years", baseFee: 95 },
];

function getTimeSlots(): TimeSlot[] {
  const now = new Date();
  const slots: TimeSlot[] = [];
  let id = 1;
  for (let i = 0; i < 30; i++) {
    const date = new Date(now);
    date.setDate(date.getDate() + i);
    const dateStr = date.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });
    const daySlots = [
      "08:00 AM - 10:00 AM",
      "10:00 AM - 12:00 PM",
      "12:00 PM - 02:00 PM",
      "02:00 PM - 04:00 PM",
    ];
    for (const time of daySlots) {
      slots.push({ id: 's' + (id++), date: dateStr, time, available: true, beforeParts: false, dateObj: new Date(date) });
    }
  }
  return slots;
}

// ΓöÇΓöÇ Props ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ

interface AIDrawerProps {
  assetId: string;
  homeId: string;
  assetName: string;
  /** Real brand (e.g. "Samsung") ΓÇö passed to all AI calls for higher accuracy */
  assetBrand?: string;
  /** Real model number ΓÇö passed to full-diagnosis and DIY calls */
  assetModel?: string;
  /**
   * Fallback brand/category when assetBrand is not available (e.g. from Maintenance page).
   * When assetBrand is provided it takes precedence over this value.
   */
  assetCategory?: string;
  onClose: () => void;
  // Direct mode - skip diagnosis and go straight to parts or booking
  directMode?: 'parts' | 'booking' | null;
  maintenanceTask?: string;
  /**
   * Parts already generated at maintenance-task creation time.
   * When provided (with directMode='parts'), the drawer reuses this list
   * and skips the `/v1/ai/generate-parts` OpenAI call.
   */
  preloadedTaskParts?: Array<{
    name: string;
    qty: number;
    description: string;
    price?: number;
  }>;
}

// ΓöÇΓöÇ Component ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ

// -- Helpers ------------------------------------------------------------------

type PhaseInfo = { phase: string; description: string };

function getInitialStep(mode: 'parts' | 'booking' | null | undefined): FlowStep {
  if (mode === 'parts') return 'parts';
  if (mode === 'booking') return 'technician';
  return 'describe';
}

type FixedPhase = { id: number; name: string; steps: string[] };

function getFixedPhases(mode: 'parts' | 'booking' | null | undefined): FixedPhase[] {
  if (mode === 'parts') {
    return [
      { id: 1, name: 'Parts', steps: ['parts'] },
      { id: 2, name: 'Details', steps: ['address', 'technician'] },
      { id: 3, name: 'Payment', steps: ['payment'] },
      { id: 4, name: 'Complete', steps: ['confirmation'] },
    ];
  }
  if (mode === 'booking') {
    return [
      { id: 1, name: 'Technician', steps: ['technician'] },
      { id: 2, name: 'Payment', steps: ['payment'] },
      { id: 3, name: 'Complete', steps: ['confirmation'] },
    ];
  }
  return [
    { id: 1, name: 'Diagnosis', steps: ['describe', 'rootCause'] },
    { id: 2, name: 'Solution', steps: ['diy', 'parts'] },
    { id: 3, name: 'Details', steps: ['address', 'technician'] },
    { id: 4, name: 'Payment', steps: ['payment'] },
    { id: 5, name: 'Complete', steps: ['confirmation'] },
  ];
}

const PHASE_LOOKUP: Record<string, Record<string, PhaseInfo>> = {
  parts: {
    parts: { phase: "Parts", description: "Select parts to order" },
    address: { phase: "Details", description: "Delivery address" },
    technician: { phase: "Details", description: "Schedule service" },
    payment: { phase: "Payment", description: "Review and pay" },
    confirmation: { phase: "Complete", description: "Booking confirmed" },
  },
  booking: {
    technician: { phase: "Technician", description: "Schedule service" },
    payment: { phase: "Payment", description: "Review and pay" },
    confirmation: { phase: "Complete", description: "Booking confirmed" },
  },
  default: {
    describe: { phase: "Diagnosis", description: "Understanding your issue" },
    rootCause: { phase: "Diagnosis", description: "Understanding your issue" },
    diy: { phase: "Solution", description: "DIY troubleshooting" },
    parts: { phase: "Solution", description: "Selecting parts" },
    address: { phase: "Details", description: "Delivery address" },
    technician: { phase: "Details", description: "Schedule service" },
    payment: { phase: "Payment", description: "Review and pay" },
    confirmation: { phase: "Complete", description: "Order confirmed" },
  },
};

interface CardInput {
  cardNumber: string;
  cardholderName: string;
  expiryMonth: string;
  expiryYear: string;
  cvv: string;
}

function validateAIDrawerCard(card: CardInput): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!card.cardNumber) {
    errors.cardNumber = 'Card number is required';
  } else if (card.cardNumber.replaceAll(/\s/g, '').length < 15) {
    errors.cardNumber = 'Card number must be 15-16 digits';
  }
  if (!card.cardholderName || card.cardholderName.trim().length < 3) {
    errors.cardholderName = 'Cardholder name is required';
  }
  if (!card.expiryMonth) {
    errors.expiryMonth = 'Month required';
  } else if (Number.parseInt(card.expiryMonth, 10) < 1 || Number.parseInt(card.expiryMonth, 10) > 12) {
    errors.expiryMonth = 'Invalid month';
  }
  if (card.expiryYear) {
    const year = Number.parseInt('20' + card.expiryYear, 10);
    const now = new Date();
    if (year < now.getFullYear() || (year === now.getFullYear() && Number.parseInt(card.expiryMonth, 10) < now.getMonth() + 1)) {
      errors.expiryYear = 'Card expired';
    }
  } else {
    errors.expiryYear = 'Year required';
  }
  if (!card.cvv) {
    errors.cvv = 'CVV required';
  } else if (card.cvv.length < 3) {
    errors.cvv = '3-4 digits';
  }
  return errors;
}

/** Step page header */
function PageHeader({ title, subtitle }: Readonly<{ title: string; subtitle?: string }>) {
  return (
    <div className="px-6 pb-3 pt-4">
      <h2 className={TYPOGRAPHY.drawer_title}>{title}</h2>
      {subtitle && <p className={TYPOGRAPHY.muted + " mt-1"}>{subtitle}</p>}
    </div>
  );
}

/** AI context badge */
function AIBadge() {
  return (
    <div className="mx-6 mb-4 inline-flex items-center gap-1.5 text-[11px] text-muted-foreground bg-muted px-2.5 py-1 rounded-full">
      <Sparkles className="h-3 w-3" />
      Powered by AI
    </div>
  );
}

function getVCConnectingStep(i: number): { iconBg: string; iconEl: React.ReactNode } {
  if (i === 0) return { iconBg: 'bg-green-500', iconEl: <Check className="w-3 h-3 text-white" /> };
  if (i === 1) return { iconBg: 'bg-green-500 animate-pulse', iconEl: <div className="w-2 h-2 rounded-full bg-white animate-ping" /> };
  return { iconBg: 'bg-muted', iconEl: null };
}

function getSlotBorderClass(slotId: string, selectedSlotId: string | undefined, available: boolean): string {
  if (selectedSlotId === slotId) return "border-primary bg-primary/5";
  if (available) return "border-border hover:border-primary/50";
  return "border-border opacity-50 cursor-not-allowed";
}

function renderSlotBadge(beforeParts: boolean | undefined, available: boolean): React.ReactNode {
  if (beforeParts) {
    return <span className="text-xs text-orange-600 font-medium px-2 py-1 bg-orange-50 rounded">Parts not arrived yet</span>;
  }
  if (available) {
    return <span className="text-xs text-green-600 font-medium flex items-center gap-1 px-2 py-1 bg-green-50 rounded"><Check className="h-3.5 w-3.5" /> RECOMMENDED</span>;
  }
  return null;
}


// ── Calendar View ─────────────────────────────────────────────────────────
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

function normalizeDate(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

function CalendarView({ timeSlots, selectedSlot, onSelectSlot, partsEta }: {
  timeSlots: TimeSlot[];
  selectedSlot: TimeSlot | null;
  onSelectSlot: (slot: TimeSlot) => void;
  partsEta?: string;
}) {
  const [calMonth, setCalMonth] = useState(() => new Date());
  const [calDate, setCalDate] = useState<string | null>(null);
  const [calTimeSlot, setCalTimeSlot] = useState<string | null>(null);
  const now = new Date();
  const year = calMonth.getFullYear();
  const month = calMonth.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startWeekday = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1;
  const daysInMonth = lastDay.getDate();
  const todayNorm = normalizeDate(now);
  const dateSlots = calDate ? timeSlots.filter(s => s.dateObj && normalizeDate(s.dateObj) === new Date(calDate).getTime()) : [];
  const isDateAvailable = (d: Date): boolean => { const norm = normalizeDate(d); if (norm < todayNorm) return false; return timeSlots.some(s => s.dateObj && normalizeDate(s.dateObj) === norm && s.available); };
  const prevMonth = () => setCalMonth(new Date(year, month - 1, 1));
  const nextMonth = () => setCalMonth(new Date(year, month + 1, 1));
  const rows: React.ReactNode[] = [];
  let cells: React.ReactNode[] = [];
  let dayCount = 1;
  for (let w = 0; w < 6; w++) {
    cells = [];
    for (let d = 0; d < 7; d++) {
      const cellDay = (w === 0 && d < startWeekday) ? 0 : (dayCount <= daysInMonth ? dayCount++ : 0);
      if (cellDay === 0) { cells.push(<div key={"e"+w+d} className="flex-1" />); }
      else {
        const cellDate = new Date(year, month, cellDay);
        const cellNorm = normalizeDate(cellDate);
        const isToday = cellNorm === todayNorm;
        const isSel = calDate && normalizeDate(new Date(calDate)) === cellNorm;
        const available = isDateAvailable(cellDate);
        cells.push(
          <button key={"d"+cellDay} disabled={!available}
            onClick={() => { if (available) { setCalDate(cellDate.toISOString()); setCalTimeSlot(null); } }}
            className={"flex-1 h-8 rounded-lg text-xs font-medium transition-all relative " + (isSel ? "ring-2 ring-primary bg-primary/10" : isToday ? "bg-primary/10" : "") + " " + (available ? "text-foreground cursor-pointer hover:bg-primary/5" : "text-muted-foreground/30 cursor-not-allowed")}
          >
            <span className={isSel ? "text-primary font-bold" : ""}>{cellDay}</span>
            {available && !isSel && <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-blue-500" />}
          </button>);
      }
    }
    rows.push(<div key={w} className="flex gap-px mb-px">{cells}</div>);
    if (dayCount > daysInMonth) break;
  }
  return (
    <div>
      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Select appointment time</p>
      {partsEta && (
        <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-yellow-50 border border-yellow-200 text-sm text-yellow-800 mb-3">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          <span>Parts arrive {partsEta}. Schedule after this date for best results.</span>
        </div>
      )}
      <div className="rounded-xl border bg-card p-3 mb-3">
        <div className="flex items-center justify-between mb-2">
          <button onClick={prevMonth} className="w-8 h-8 rounded-full bg-muted flex items-center justify-center hover:bg-muted/80 transition-colors"><ChevronLeft className="w-5 h-5 text-primary" /></button>
          <span className="text-sm font-bold text-foreground">{MONTHS[month]} {year}</span>
          <button onClick={nextMonth} className="w-8 h-8 rounded-full bg-muted flex items-center justify-center hover:bg-muted/80 transition-colors"><ChevronRight className="w-5 h-5 text-primary" /></button>
        </div>
        <div className="flex gap-0.5 mb-2">
          {WEEKDAYS.map(d => <div key={d} className="flex-1 text-center text-xs font-semibold text-muted-foreground">{d}</div>)}
        </div>
        {rows}
      </div>
      {calDate && (
        <div>
          <p className="text-xs font-semibold text-foreground mb-2">Available Time Slots for {new Date(calDate).toLocaleDateString("en-US", { month: "numeric", day: "numeric", year: "numeric" })}</p>
          <div className="grid grid-cols-2 gap-2">
            {dateSlots.length > 0 ? dateSlots.map((slot) => (
              <button key={slot.id}
                onClick={() => { setCalTimeSlot(slot.id); onSelectSlot(slot); }}
                className={"px-3 py-2.5 rounded-xl border text-xs font-medium transition-all " + (calTimeSlot === slot.id ? "border-primary bg-primary/5 text-primary ring-1 ring-primary" : "border-border bg-card text-foreground hover:border-primary/50")}
              >{slot.time}</button>
            )) : <p className="text-sm text-muted-foreground col-span-2 py-4 text-center">No time slots available</p>}
          </div>
        </div>
      )}
    </div>
  );
}

export function AIDrawer({ assetId, homeId, assetName, assetBrand, assetModel, assetCategory = "", onClose, directMode = null, maintenanceTask, preloadedTaskParts }: Readonly<AIDrawerProps>) { // NOSONAR
  // Resolve the best available brand: explicit assetBrand > assetCategory fallback
  const resolvedBrand = (assetBrand?.trim() || assetCategory || 'Unknown');
  // Flow
  const initialStep: FlowStep = getInitialStep(directMode);
  const [step, setStep] = useState<FlowStep>(initialStep);
  const [loading, setLoading] = useState(false);
  const [issuesLoading, setIssuesLoading] = useState(!directMode);

  // Data
  const [commonIssues, setCommonIssues] = useState<CommonIssue[]>([]);
  const [selectedIssue, setSelectedIssue] = useState(maintenanceTask || "");
  const [selectedIssues, setSelectedIssues] = useState<string[]>(maintenanceTask ? [maintenanceTask] : []);
  const [customDescription, setCustomDescription] = useState(maintenanceTask || "");
  const [rootCauses, setRootCauses] = useState<RootCause[]>([]);
  const [diagnosisIssueTitle, setDiagnosisIssueTitle] = useState('');
  const [diagnosisQuickChecks, setDiagnosisQuickChecks] = useState<string[]>([]);
  const [diagnosisSeverity, setDiagnosisSeverity] = useState('');
  const [diagnosisEstimatedCost, setDiagnosisEstimatedCost] = useState('');
  const [diagnosisRequiresProfessional, setDiagnosisRequiresProfessional] = useState(false);
  const [solutionChoice, setSolutionChoice] = useState<"diy" | "technician" | null>(directMode ? "technician" : null);
  const [diySteps, setDiySteps] = useState<DIYStep[]>([]);
  const [currentDiyIndex, setCurrentDiyIndex] = useState(0);
  const [diyResults, setDiyResults] = useState<Record<number, "working" | "not-working">>({});
  const [parts, setParts] = useState<Part[]>([]);
  const [selectedParts, setSelectedParts] = useState<Set<string>>(new Set());
  const [selectedTech, setSelectedTech] = useState<Technician | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
  const [acceptedPolicy, setAcceptedPolicy] = useState(false);
  const [bookingMode, setBookingMode] = useState<"parts" | "tech" | "both" | null>(null);

  const [orderId, setOrderId] = useState("");
  const [bookingId, setBookingId] = useState("");
  const [apiError, setApiError] = useState<string | null>(null);
  const [showCostDetails, setShowCostDetails] = useState(false);
  
  // Inline editing states
  const [editingAddress, setEditingAddress] = useState(false);
  const [selectingHome, setSelectingHome] = useState(false);
  
  // Edited address state (stores user edits)
  const [editedAddress, setEditedAddress] = useState<{
    address: string;
    city: string;
    state: string;
    zip: string;
    country: string;
  } | null>(null);
  
  // Payment card management state ΓÇö backed by backend API
  const [paymentCards, setPaymentCards] = useState<SavedCard[]>([]);
  const [addingCard, setAddingCard] = useState(false);
  const [newCard, setNewCard] = useState({
    cardNumber: '',
    expiryMonth: '',
    expiryYear: '',
    cvv: '',
    cardholderName: ''
  });
  const [selectedCard, setSelectedCard] = useState<string>('');
  const [cardErrors, setCardErrors] = useState<{
    cardNumber?: string;
    cardholderName?: string;
    expiryMonth?: string;
    expiryYear?: string;
    cvv?: string;
  }>({}); 
  
  // Get properties from context
  const { properties, currentProperty, switchProperty } = useProperty();

  // Tax rate is loaded from the backend (was hardcoded 0.08).
  const [taxRate, setTaxRate] = useState<number>(0); // 0 until rules load
  useEffect(() => {
    let mounted = true;
    getBusinessRules()
      .then((r) => mounted && setTaxRate(r.taxRate))
      .catch(() => mounted && setTaxRate(0));
    return () => { mounted = false; };
  }, []);

  // Computed - Cost Estimator (tax rate now comes from backend)
  const partsTotal = parts.filter(p => selectedParts.has(p.id)).reduce((s, p) => s + p.price * p.qty, 0);
  const shipping = selectedParts.size > 0 ? 9.99 : 0;
  const tax = +(partsTotal * taxRate).toFixed(2);
  const techFee = selectedTech?.baseFee ?? 0;
  const laborCost = selectedTech ? selectedTech.baseFee : 0;
  const estimatedHours = selectedTech ? 2 : 0;
  const hourlyRate = selectedTech ? techFee / 2 : 0;
  const grandTotal = partsTotal + shipping + tax + (bookingMode === "parts" ? 0 : techFee);
  const partsEta = (() => { const d = new Date(); d.setDate(d.getDate() + 6); return d.toLocaleDateString("en-US"); })();

  // Load parts directly if in direct parts mode
  useEffect(() => {
    if (directMode === 'parts' && maintenanceTask) {
      loadPartsForMaintenance();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [directMode, maintenanceTask]);

  // Load technician slots directly if in direct booking mode
  useEffect(() => {
    if (directMode === 'booking') {
      setTimeSlots(getTimeSlots());
      setBookingMode('tech');
    }
  }, [directMode]);

  const loadPartsForMaintenance = async () => {
    // Reuse parts already generated when the maintenance task was created.
    // Avoids a redundant OpenAI call on every drawer open.
    if (preloadedTaskParts && preloadedTaskParts.length > 0) {
      const mapped: Part[] = preloadedTaskParts.map((p, i) => ({
        id: `p${i}`,
        name: p.name,
        description: p.description || '',
        price: typeof p.price === 'number' ? p.price : 0,
        qty: p.qty || 1,
      }));
      setParts(mapped);
      setPreloadedParts(mapped);
      setSelectedParts(new Set(mapped.map((x) => x.id)));
      return;
    }
    setLoading(true);
    try {
      const res = await api.post<{ parts: Part[] }>('/v1/ai/generate-parts', {
        assetName,
        assetBrand: resolvedBrand,
        problem: maintenanceTask || 'Maintenance required',
      });
      if (Array.isArray(res?.parts)) {
        setParts(res.parts.map((p, i) => ({ ...p, id: `p${i}` })));
      }
    } catch (error) {
      console.error('Failed to load parts:', error);
      // Fallback to empty parts list
      setParts([]);
    } finally {
      setLoading(false);
    }
  };

  // Load common issues (real AI) once we have asset context
  useEffect(() => {
    if (directMode) return; // Skip if in direct mode
    let cancelled = false;
    async function load() {
      setIssuesLoading(true);
      try {
        const res = await api.post<{ issues: (string | { title: string })[] }>('/v1/ai/generate-issue-options', {
          assetName,
          assetBrand: resolvedBrand,
        });
        if (!cancelled && Array.isArray(res?.issues)) {
          setCommonIssues(res.issues.map((it: string | { title: string }, i: number) => ({
            id: String(i),
            title: typeof it === 'string' ? it : it.title,
          })));
        } else if (!cancelled) {
          setCommonIssues([]);
        }
      } catch {
        if (!cancelled) setCommonIssues([]);
      } finally {
        if (!cancelled) setIssuesLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [assetName, assetCategory, directMode]);

  // Load saved payment methods from backend
  useEffect(() => {
    let cancelled = false;
    getPaymentMethods()
      .then((methods) => {
        if (cancelled) return;
        setPaymentCards(methods);
        const def = methods.find((m) => m.isDefault);
        if (def) setSelectedCard(def.id);
        else if (methods.length > 0) setSelectedCard(methods[0].id);
      })
      .catch(() => { /* user may not have cards yet */ });
    return () => { cancelled = true; };
  }, []);

  // Reset edited address when switching properties
  useEffect(() => {
    setEditedAddress(null);
  }, [currentProperty?.id]);

  // Fixed phases for consistent stepper
  const FIXED_PHASES = getFixedPhases(directMode);

  // Get current phase number based on current step
  const getCurrentPhase = (): number => {
    for (const phase of FIXED_PHASES) {
      if (phase.steps.includes(step)) return phase.id;
    }
    return 1;
  };

  // Get phase info for display
  const _getPhaseInfo = (): PhaseInfo => {
    const modeKey = directMode ?? 'default';
    return PHASE_LOOKUP[modeKey]?.[step] ?? { phase: "Progress", description: "" };
  };

  const currentPhase = getCurrentPhase();

  // Step navigation
  const getVisibleSteps = useCallback((): FlowStep[] => {
    // Direct parts mode: skip diagnosis steps entirely
    if (directMode === 'parts') {
      const s: FlowStep[] = ["parts", "address"];
      // Add technician step if booking tech
      if (bookingMode === "both" || bookingMode === "tech") {
        s.push("technician");
      }
      s.push("payment", "confirmation");
      return s;
    }
    
    // Direct booking mode: skip diagnosis and parts, go straight to booking
    if (directMode === 'booking') {
      return ["technician", "payment", "confirmation"];
    }
    
    // Normal AI diagnosis flow
    const s: FlowStep[] = ["describe", "rootCause"];
    if (solutionChoice === "diy") {
      s.push("diy", "parts");
      if (bookingMode === "both" || bookingMode === "tech") s.push("address", "technician", "payment", "confirmation");
      else if (bookingMode === "parts") s.push("address", "payment", "confirmation");
    } else if (solutionChoice === "technician") {
      if (bookingMode === "tech" && selectedTech) {
        // From Virtual Consult "Book In-Person" ΓÇö tech already chosen, skip parts + technician steps
        s.push("address", "payment", "confirmation");
      } else if (bookingMode === "tech") {
        s.push("address", "technician", "payment", "confirmation");
      } else {
        s.push("parts", "address", "technician", "payment", "confirmation");
      }
    }
    return s;
  }, [solutionChoice, bookingMode, directMode, selectedTech]);

  const visible = getVisibleSteps();
  const stepIdx = visible.indexOf(step);
  const _total = visible.length;

  const goBack = () => { if (stepIdx > 0) setStep(visible[stepIdx - 1]); };
  const canGoBack = stepIdx > 0 && step !== "confirmation";

  // Simulate AI delay (used for steps that don't need backend)
  const [preloadedDiy, setPreloadedDiy] = useState<DIYStep[]>([]);
  const [preloadedParts, setPreloadedParts] = useState<Part[]>([]);

  // Handlers
  const handleIssueContinue = async () => {
    const issue = selectedIssues.length > 0 ? selectedIssues.join(', ') : customDescription;
    if (!issue) return;
    setLoading(true);
    try {
      const res = await api.post<{
        possibleCauses?: string[];
        causes?: (string | { title: string })[];
        rootCauses?: (string | { title: string })[];
        diySteps?: { title: string; description: string }[];
        parts?: { name: string; description: string; price: number }[];
        severity?: string;
        estimatedConsultMinutes?: number;
      }>(
        '/v1/ai/full-diagnosis',
        {
          assetName,
          assetBrand: resolvedBrand,
          ...(assetModel?.trim() ? { assetModel: assetModel.trim() } : {}),
          issue,
          ...(customDescription?.trim() && customDescription !== issue ? { additionalDescription: customDescription.trim() } : {}),
        }
      );
      const raw: (string | { title: string })[] =
        res?.possibleCauses || res?.causes || res?.rootCauses || [];
      setRootCauses(
        Array.isArray(raw) && raw.length > 0
          ? raw.map((r: string | { title: string }, i: number) => ({
              id: `rc${i}`,
              title: typeof r === 'string' ? r : r.title,
            }))
          : []
      );
      setDiagnosisIssueTitle((res as any)?.issueTitle || (res as any)?.diagnosis || '');
      setDiagnosisQuickChecks(Array.isArray((res as any)?.quickChecks) ? (res as any).quickChecks : []);
      setDiagnosisSeverity(((res as any)?.severity || '').toLowerCase());
      setDiagnosisEstimatedCost((res as any)?.estimatedCost || '');
      setDiagnosisRequiresProfessional((res as any)?.requiresProfessional === true);
      // Pre-load DIY steps and parts from full-diagnosis response
      if (Array.isArray(res?.diySteps) && res.diySteps.length > 0) {
        setPreloadedDiy(res.diySteps.map((s, i) => ({ id: i, title: s.title, description: s.description })));
      }
      if (Array.isArray(res?.parts) && res.parts.length > 0) {
        setPreloadedParts(res.parts.map((p, i) => ({
          id: `p${i}`, name: p.name, description: p.description || '', price: p.price || 0, qty: 1,
        })));
      }
      setStep('rootCause');
    } catch (err) {
      console.error('[AIDrawer] full-diagnosis failed:', err);
      // Do NOT navigate forward ∩┐╜ show error in place
    } finally {
      setLoading(false);
    }
  };

  const fetchAndSetDIYSteps = async () => {
    if (preloadedDiy.length > 0) {
      setDiySteps(preloadedDiy);
    } else {
      setLoading(true);
      try {
        const issue = selectedIssue || customDescription;
        const res = await api.post<{ steps?: { title: string; description: string }[] }>(
          '/v1/ai/generate-diy-troubleshooting',
          {
            assetName,
            assetBrand: resolvedBrand,
            ...(assetModel?.trim() ? { assetModel: assetModel.trim() } : {}),
            issue,
          }
        );
        if (Array.isArray(res?.steps) && res.steps.length > 0) {
          setDiySteps(res.steps.map((s, i) => ({ id: i, title: s.title, description: s.description })));
        } else {
          setDiySteps([]);
        }
      } catch (err) {
        console.error('[AIDrawer] generate-diy-troubleshooting failed:', err);
        setDiySteps([]);
      } finally {
        setLoading(false);
      }
    }
  };

  const fetchAndSetParts = async () => {
    if (preloadedParts.length > 0) {
      setParts(preloadedParts);
      setSelectedParts(new Set(preloadedParts.map((x) => x.id)));
    } else {
      setLoading(true);
      try {
        const issue = selectedIssue || customDescription;
        const res = await api.post<{ parts?: { name: string; description: string; price: number }[] }>(
          '/v1/ai/generate-parts',
          { assetName, assetBrand: resolvedBrand, ...(assetModel?.trim() ? { assetModel: assetModel.trim() } : {}), problem: issue }
        );
        if (Array.isArray(res?.parts) && res.parts.length > 0) {
          const p = res.parts.map((part, i) => ({
            id: `p${i}`, name: part.name, description: part.description || '', price: part.price || 0, qty: 1,
          }));
          setParts(p);
          setSelectedParts(new Set(p.map((x) => x.id)));
        } else {
          setParts([]);
        }
      } catch (err) {
        console.error('[AIDrawer] generate-parts failed:', err);
        setParts([]);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSolutionChoice = async (choice: "diy" | "technician") => {
    setSolutionChoice(choice);
    if (choice === "diy") {
      await fetchAndSetDIYSteps();
      setCurrentDiyIndex(0);
      setDiyResults({});
      setStep('diy');
    } else {
      await fetchAndSetParts();
      setStep('parts');
    }
  };

  // Used from Virtual Consult "Book an In-Person Visit" — skips parts, goes straight to booking a technician
  const handleBookTechOnly = () => {
    setSolutionChoice("technician");
    setBookingMode("tech");
    setStep("address");
  };

  const handleDiyStepResult = (result: "working" | "not-working") => {
    const newResults = { ...diyResults, [currentDiyIndex]: result };
    setDiyResults(newResults);
  };

  const fetchPartsForDIYFix = async (issue: string) => {
    setLoading(true);
    try {
      const res = await api.post<{ parts?: { name: string; description: string; price: number }[] }>(
        '/v1/ai/generate-parts',
        { assetName, assetBrand: resolvedBrand, problem: issue }
      );
      if (Array.isArray(res?.parts) && res.parts.length > 0) {
        const p = res.parts.map((part: { name: string; description: string; price: number }, i: number) => ({
          id: `p${i}`,
          name: part.name,
          description: part.description || '',
          price: part.price || 0,
          qty: 1,
        }));
        setParts(p);
        setSelectedParts(new Set(p.map((x) => x.id)));
      } else if (preloadedParts.length > 0) {
        setParts(preloadedParts);
        setSelectedParts(new Set(preloadedParts.map((x) => x.id)));
      } else {
        setParts([]);
      }
    } catch {
      if (preloadedParts.length > 0) {
        setParts(preloadedParts);
        setSelectedParts(new Set(preloadedParts.map((x) => x.id)));
      } else {
        setParts([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDiyNext = async () => {
    if (currentDiyIndex < diySteps.length - 1) {
      setCurrentDiyIndex(currentDiyIndex + 1);
    } else {
      // All steps done - check if any are not working
      const notWorkingStepIndices = Object.entries(diyResults)
        .filter(([, result]) => result === "not-working")
        .map(([idx]) => Number.parseInt(idx, 10));

      if (notWorkingStepIndices.length > 0) {
        const issue = selectedIssue || customDescription;
        await fetchPartsForDIYFix(issue);
        setStep('parts');
      } else {
        onClose();
      }
    }
  };

  const handlePartsAction = (mode: "parts" | "tech" | "both") => {
    setBookingMode(mode);
    setTimeSlots(getTimeSlots());
    if (mode === "tech") setSelectedParts(new Set());
    // In direct booking mode, skip address and go straight to technician
    if (directMode === 'booking') {
      setStep("technician");
    } else {
      setStep("address");
    }
  };

  const handleAddressContinue = () => {
    if (bookingMode === "parts") return setStep("payment");
    if (bookingMode === "tech" && selectedTech) return setStep("payment"); // tech pre-selected from VC
    setStep("technician");
  };
  const handleTechContinue = () => { if (selectedTech && selectedSlot) setStep("payment"); };
  
  const handlePlaceOrder = async () => {
    setLoading(true);
    setApiError(null);

    try {
      if (!assetId || !homeId) throw new Error('Missing asset or home information');

      const issue = selectedIssue || customDescription;
      const selectedPartsList = parts.filter((p) => selectedParts.has(p.id));
      const hasParts = selectedPartsList.length > 0;
      const hasBooking = !!(selectedTech && selectedSlot && bookingMode !== "parts");

      // Build shipping address string
      const address = editedAddress || {
        address: currentProperty?.address || '123 Main Street',
        city: currentProperty?.city || 'New York',
        state: currentProperty?.state || 'NY',
        zip: currentProperty?.zip || '10001',
        country: currentProperty?.country || 'United States',
      };
      const shippingAddress = `${address.address}, ${address.city}, ${address.state} ${address.zip}, ${address.country}`;

      const diagnosisData = {
        issue,
        rootCauses: rootCauses.map((rc) => rc.title),
        diyResults,
        diyAttempted: solutionChoice === "diy",
        parts: selectedPartsList.map((p) => p.name),
      };

      const result = await aiCheckout({
        homeId,
        assetId,
        diagnosisIssue: issue,
        diagnosisData,
        shippingAddress: hasParts ? shippingAddress : undefined,
        parts: hasParts ? selectedPartsList : undefined,
        subtotal: hasParts ? partsTotal : undefined,
        taxAmount: hasParts ? tax : undefined,
        shippingCost: hasParts ? shipping : undefined,
        totalAmount: hasParts ? partsTotal + tax + shipping : undefined,
        booking: (hasBooking && selectedTech && selectedSlot)
          ? {
              serviceType: "appliance_repair",
              scheduledDate: new Date(selectedSlot.date).toISOString(),
              timeSlot: selectedSlot.time,
              technicianId: selectedTech.id,
              technicianName: selectedTech.name,
              serviceFee: selectedTech.baseFee,
              notes: `Issue: ${issue}\nRoot Causes: ${rootCauses.map((rc) => rc.title).join(', ')}`,
            }
          : undefined,
      });

      setOrderId(result.orderId || '');
      setBookingId(result.bookingId || '');
      setStep("confirmation");

    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string; error?: string } }; message?: string };
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Failed to place order. Please try again.";
      setApiError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const togglePart = (id: string) =>
    setSelectedParts(prev => { const n = new Set(prev); if (n.has(id)) { n.delete(id); } else { n.add(id); } return n; });

  // Address & Payment Handlers
  const handleEditAddress = () => {
    if (editingAddress) {
      // Saving - just close the edit mode (values already saved in state)
      setEditingAddress(false);
    } else {
      // Entering edit mode - initialize with current values ONLY if editedAddress is null
      if (!editedAddress) {
        setEditedAddress({
          address: currentProperty?.address || '123 Main Street',
          city: currentProperty?.city || 'New York',
          state: currentProperty?.state || 'NY',
          zip: currentProperty?.zip || '10001',
          country: currentProperty?.country || 'United States',
        });
      }
      setEditingAddress(true);
    }
  };

  const handleSwitchHome = () => {
    setSelectingHome(!selectingHome);
  };

  const handleAddCard = () => {
    setAddingCard(true);
  };

  const handleSaveNewCard = async () => {
    // Validate locally before hitting backend
    const errors = validateAIDrawerCard(newCard);
    if (Object.keys(errors).length > 0) {
      setCardErrors(errors);
      return;
    }
    setCardErrors({});

    // ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ
    // NOTE: In production use Stripe.js (stripe.createPaymentMethod) to
    // tokenize the card before sending to the server ΓÇö never send raw card
    // numbers to your backend (PCI DSS violation).
    //
    // For the current dev/test environment we use the Stripe test token
    // pm_card_visa (a static test PaymentMethod id) so the flow can be
    // exercised end-to-end without a real Stripe integration in the UI.
    // Replace this with a proper Stripe Elements / confirmSetup flow before
    // going to production.
    // ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ
    const testPmId = 'pm_card_visa'; // replace with real Stripe.js token in production

    // Derive safe card metadata (never raw card number ΓÇö only brand/last4/expiry)
    const firstDigit = newCard.cardNumber.replaceAll(/\s/g, '')[0];
    let inferredBrand = 'card';
    if (firstDigit === '4') inferredBrand = 'visa';
    else if (firstDigit === '5') inferredBrand = 'mastercard';
    else if (firstDigit === '3') inferredBrand = 'amex';
    const inferredLast4 = newCard.cardNumber.replaceAll(/\s/g, '').slice(-4);
    const inferredExpMonth = Number.parseInt(newCard.expiryMonth, 10);
    const inferredExpYear = Number.parseInt('20' + newCard.expiryYear, 10);

    try {
      setLoading(true);
      const saved = await attachPaymentMethod({
        stripePaymentMethodId: testPmId,
        isDefault: paymentCards.length === 0,
        // Safe fallback metadata used when Stripe is unavailable (dev/test)
        brand: inferredBrand,
        last4: inferredLast4,
        expMonth: inferredExpMonth,
        expYear: inferredExpYear,
        billingName: newCard.cardholderName || undefined,
      });
      setPaymentCards((prev) => [...prev, saved]);
      setSelectedCard(saved.id);
    } catch {
      // Last-resort local placeholder (no DB persistence) ΓÇö only if backend is down
      const placeholder: SavedCard = {
        id: Date.now().toString(),
        userId: '',
        stripePaymentMethodId: `local_${Date.now()}`,
        brand: inferredBrand,
        last4: inferredLast4,
        expMonth: inferredExpMonth,
        expYear: inferredExpYear,
        billingName: newCard.cardholderName,
        isDefault: paymentCards.length === 0,
        isActive: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      setPaymentCards((prev) => [...prev, placeholder]);
      setSelectedCard(placeholder.id);
    } finally {
      setLoading(false);
    }

    setNewCard({ cardNumber: '', expiryMonth: '', expiryYear: '', cvv: '', cardholderName: '' });
    setAddingCard(false);
  };

  const handleDeleteCard = async (cardId: string) => {
    if (paymentCards.length === 1) {
      alert('You must have at least one payment method');
      return;
    }
    try {
      await deletePaymentMethod(cardId);
      const updated = paymentCards.filter((card) => card.id !== cardId);
      setPaymentCards(updated);
      if (selectedCard === cardId) {
        const next = updated.find((c) => c.isDefault) || updated[0];
        setSelectedCard(next?.id || '');
      }
    } catch {
      // silently keep state unchanged ΓÇô backend guards against removing the last card
    }
  };

  const _handleSetDefaultCard = async (cardId: string) => {
    try {
      const updated = await setDefaultPaymentMethod(cardId);
      setPaymentCards((prev) =>
        prev.map((c) => ({ ...c, isDefault: c.id === updated.id }))
      );
      setSelectedCard(cardId);
    } catch {
      // optimistic update as fallback
      setPaymentCards((prev) =>
        prev.map((c) => ({ ...c, isDefault: c.id === cardId }))
      );
      setSelectedCard(cardId);
    }
  };

  const _handleEditPaymentMethod = () => {
    handleAddCard();
  };

  // ΓöÇΓöÇ Render ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ

  return (
    <>
      {/* Backdrop */}
      <button type="button" className={getDrawerBackdropClass(true)} onClick={onClose} aria-label="Close drawer" />

      {/* Drawer */}
      <div className={cn(getDrawerPanelClass(true, 'lg'), "animate-slide-in-right")}>

        {/* ΓöÇΓöÇ Header ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ */}
        <div className="flex items-center justify-between px-6 py-4 border-b shrink-0 bg-muted/30 shadow-sm">
          <div className="flex items-center gap-2">
            {canGoBack && (
              <button onClick={goBack} className="w-8 h-8 rounded-lg hover:bg-secondary flex items-center justify-center transition-colors">
                <ChevronLeft className="h-4 w-4" />
              </button>
            )}
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-secondary flex items-center justify-center transition-colors">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* ΓöÇΓöÇ Step Indicator ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ */}
        <div className="px-6 pt-3 pb-3 bg-muted/20">
          <div className="flex gap-1.5">
            {FIXED_PHASES.map((phase) => (
              <div
                key={phase.id}
                className={cn(
                  "h-1 flex-1 rounded-full transition-all duration-500",
                  phase.id <= currentPhase ? "bg-primary" : "bg-muted"
                )}
              />
            ))}
          </div>
        </div>

        {/* ΓöÇΓöÇ Content ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ */}
        <div className="flex-1 overflow-y-auto">

          {/* Loading */}
          {loading && (
            <div className="flex flex-col items-center justify-center py-24 gap-3 animate-fade-in">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Analyzing with AI&hellip;</p>
            </div>
          )}

          {/* ΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉ */}
          {/* STEP 1 ΓÇö Describe Your Issue                   */}
          {/* ΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉ */}
          {!loading && step === "describe" && (
            <div className="animate-fade-in-up">
              <PageHeader title="Describe Your Issue" subtitle={`Tell us what\u2019s wrong with your ${assetName}`} />
              <AIBadge />

              <div className="px-6 pb-6 space-y-6">
                {/* Common issues */}
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">Common issues</p>
                  {issuesLoading ? (
                    <div className="flex items-center gap-2 py-4 text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin shrink-0" />
                      <span className="text-sm">Loading issues for your {assetName}&hellip;</span>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {commonIssues.map((issue, idx) => (
                        <button
                          key={issue.id}
                          onClick={() => { setSelectedIssues(prev => prev.includes(issue.title) ? prev.filter(i => i !== issue.title) : [...prev, issue.title]); setSelectedIssue(issue.title); setCustomDescription(""); }}
                          style={{ animationDelay: `${idx * 0.06}s` }}
                          className={cn(
                            "animate-fade-in-up w-full text-left px-4 py-3.5 rounded-xl border transition-all duration-200",
                            selectedIssues.includes(issue.title) || selectedIssue === issue.title
                              ? "border-primary bg-primary/5"
                              : "border-border hover:border-primary/50 hover:bg-primary/[0.03]"
                          )}
                        >
                          <span className="text-sm">{idx + 1}. {issue.title}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Custom description */}
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">Or describe in your own words</p>
                  <div className="relative">
                    <textarea
                      id="voice-textarea"
                      value={customDescription}
                      onChange={e => { setCustomDescription(e.target.value); setSelectedIssue(""); }}
                      placeholder="Example: Making loud humming noise and not cooling properly&hellip;"
                      className="w-full h-24 px-4 py-3 rounded-xl border border-input bg-transparent text-sm resize-none placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring transition-colors"
                    />
                    <button
                      id="voice-btn"
                      onClick={() => {
                        const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
                        if (!SpeechRecognition) return;
                        const recognition = new SpeechRecognition();
                        recognition.lang = 'en-US';
                        recognition.onresult = (event: any) => {
                          const t = Array.from(event.results as Array<{ [index: number]: { transcript: string } }>).map(r => r[0].transcript).join('');
                          const el = document.getElementById('voice-textarea') as HTMLTextAreaElement | null;
                          if (el) el.value = t;
                          setCustomDescription(t);
                        };
                        recognition.start();
                      }}
                      className="absolute bottom-2 right-2 p-2 rounded-full bg-muted hover:bg-primary/10 hover:text-primary transition-all"
                      title="Start voice input"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 10v2a7 7 0 01-14 0v-2" />
                        <line x1="12" y1="19" x2="12" y2="23" />
                        <line x1="8" y1="23" x2="16" y2="23" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Actions */}
                <div className="space-y-4">
                  <div className="rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/20 p-4">
                    <div className="flex items-start gap-3">
                      <svg className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-amber-800 dark:text-amber-300">Is this an emergency?</p>
                        <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">If there’s a safety concern (gas leak, electrical issue, water damage), skip the diagnosis and book a technician immediately.</p>
                      </div>
                    </div>
                  </div>
                  <Button
                    size="lg"
                    className="w-full"
                    disabled={selectedIssues.length === 0 && !customDescription.trim()}
                    onClick={handleIssueContinue}
                  >
                    Continue
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* ΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉ */}
          {/* STEP 2 ΓÇö Possible Issues Found                 */}
          {/* ΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉ */}
          {!loading && step === "rootCause" && (
            <div className="animate-fade-in-up">
              <div className="px-6 pb-4 space-y-4">
                {resolvedBrand && <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{resolvedBrand}</p>}
                <h2 className="text-xl font-bold text-foreground leading-tight">{diagnosisIssueTitle || "Possible Issues Found"}</h2>
                {(diagnosisSeverity || diagnosisEstimatedCost) && (
                  <div className="flex items-center gap-2 flex-wrap">
                    {diagnosisSeverity && (
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border ${
                        ['critical','high'].includes(diagnosisSeverity)
                          ? 'text-red-700 bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800'
                          : diagnosisSeverity === 'medium'
                          ? 'text-amber-700 bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800'
                          : 'text-green-700 bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800'
                      }`}>
                        <AlertTriangle className="w-3.5 h-3.5" />
                        {diagnosisSeverity === 'critical' ? 'Critical' : diagnosisSeverity === 'high' ? 'High' : diagnosisSeverity === 'medium' ? 'Medium' : 'Low'} Severity
                      </span>
                    )}
                    {diagnosisEstimatedCost && (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold text-muted-foreground bg-muted border border-border">
                        <Shield className="w-3.5 h-3.5" />
                        Est. cost: {diagnosisEstimatedCost}
                      </span>
                    )}
                  </div>
                )}
                {rootCauses.length > 0 && (
                  <div className="rounded-xl border bg-card overflow-hidden">
                    <div className="px-3 pt-2.5 pb-1">
                      <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-[0.08em] font-sans">WHY THIS HAPPENS</span>
                    </div>
                    <div className="px-3 pb-1">
                      {rootCauses.map((rc, i) => (
                        <div key={rc.id} className={`flex items-start gap-3 py-1.5 ${i < rootCauses.length - 1 ? 'border-b border-border/50' : ''}`}>
                          <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                          <p className="text-sm text-foreground leading-relaxed flex-1">{rc.title}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {diagnosisQuickChecks.length > 0 && (
                  <div className="rounded-xl border bg-card overflow-hidden">
                    <div className="px-3 pt-2.5 pb-1">
                      <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-[0.08em] font-sans">WHAT TO CHECK NOW</span>
                    </div>
                    <div className="px-3 pb-2">
                      {diagnosisQuickChecks.map((check, i) => (
                        <div key={i} className={`flex items-start gap-3 py-1.5 ${i < diagnosisQuickChecks.length - 1 ? 'border-b border-border/50' : ''}`}>
                          <span className="w-1.5 h-1.5 rounded-full bg-green-500 mt-2 flex-shrink-0" />
                          <p className="text-sm text-foreground leading-relaxed flex-1">{check}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div>
                  <p className="text-xs font-semibold text-foreground mb-2">Choose your solution</p>
                  <div className="grid grid-cols-3 gap-2">
                    <button
                      onClick={() => handleSolutionChoice("diy")}
                      className={`group p-4 rounded-xl border-2 transition-all duration-200 text-left flex flex-col items-center text-center active:scale-[0.97] ${
                        !diagnosisRequiresProfessional
                          ? 'border-primary bg-primary/5 shadow-md'
                          : 'border-border bg-card hover:border-primary hover:bg-primary/5 hover:shadow-md'
                      }`}>
                      <div className={`w-11 h-11 rounded-lg flex items-center justify-center mb-2.5 group-hover:scale-105 transition-all duration-200 ${
                        !diagnosisRequiresProfessional
                          ? 'bg-primary'
                          : 'bg-muted group-hover:bg-primary'
                      }`}>
                        <Wrench className={`w-5 h-5 transition-colors duration-200 ${
                          !diagnosisRequiresProfessional ? 'text-white' : 'text-foreground group-hover:text-white'
                        }`} />
                      </div>
                      <p className="text-sm font-semibold text-foreground mb-1 leading-tight">DIY Fix</p>
                      <p className="text-[11px] text-muted-foreground leading-tight">Step-by-step guide</p>
                    </button>
                    <button
                      onClick={() => handleSolutionChoice("technician")}
                      className={`group p-4 rounded-xl border-2 transition-all duration-200 text-left flex flex-col items-center text-center active:scale-[0.97] ${
                        diagnosisRequiresProfessional
                          ? 'border-primary bg-primary/5 shadow-md'
                          : 'border-border bg-card hover:border-primary hover:bg-primary/5 hover:shadow-md'
                      }`}>
                      <div className={`w-11 h-11 rounded-lg flex items-center justify-center mb-2.5 group-hover:scale-105 transition-all duration-200 ${
                        diagnosisRequiresProfessional
                          ? 'bg-primary'
                          : 'bg-muted group-hover:bg-primary'
                      }`}>
                        <User className={`w-5 h-5 transition-colors duration-200 ${
                          diagnosisRequiresProfessional ? 'text-white' : 'text-foreground group-hover:text-white'
                        }`} />
                      </div>
                      <p className="text-sm font-semibold text-foreground mb-1 leading-tight">Book Technician</p>
                      <p className="text-[11px] text-muted-foreground leading-tight">Professional service</p>
                    </button>
                    <button
                      onClick={() => setStep('virtual-consult')}
                      className="group p-4 bg-card border-2 border-green-500/30 rounded-xl hover:border-green-500 hover:bg-green-500/5 hover:shadow-md active:scale-[0.97] transition-all duration-200 text-left flex flex-col items-center text-center relative">
                      <span className="absolute top-2 right-2 flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                      </span>
                      <div className="w-11 h-11 bg-green-500/10 rounded-lg flex items-center justify-center mb-2.5 group-hover:bg-green-500 group-hover:scale-105 transition-all duration-200">
                        <Video className="w-5 h-5 text-green-600 dark:text-green-400 group-hover:text-white transition-colors duration-200" />
                      </div>
                      <p className="text-sm font-semibold text-foreground mb-1 group-hover:text-green-600 dark:group-hover:text-green-400 transition-colors leading-tight">Virtual Consult</p>
                      <p className="text-[11px] text-muted-foreground leading-tight">Live video with expert</p>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ══════════════════════════════════════════════ */}
          {/* STEP 2b — Virtual Consult (video call)          */}
          {/* ══════════════════════════════════════════════ */}
          {!loading && step === "virtual-consult" && (
            <div className="animate-fade-in-up">
              <PageHeader
                title="Virtual Consult"
                subtitle="Live video call with a technician"
              />
              <div className="px-6 pb-6">
                <VirtualConsultStep
                  asset={{ name: assetName, brand: resolvedBrand }}
                  aiSolution={
                    [...rootCauses.map((rc) => rc.title), ...diagnosisQuickChecks].join('\n') ||
                    diagnosisIssueTitle ||
                    selectedIssue ||
                    customDescription
                  }
                  onClose={onClose}
                  onBookFollowUp={handleBookTechOnly}
                />
              </div>
            </div>
          )}

          {!loading && step === "diy" && diySteps.length > 0 && (
            <div className="animate-fade-in-up">
              <PageHeader 
                title="DIY Troubleshooting" 
                subtitle={`Step ${currentDiyIndex + 1} of ${diySteps.length}`} 
              />
              <AIBadge />

              <div className="px-6 pb-6 space-y-6">
                {/* Current Step Card */}
                <div key={currentDiyIndex} className="rounded-xl border border-border bg-muted/20 p-5 animate-fade-in-up">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="h-10 w-10 rounded-lg bg-muted border border-border flex items-center justify-center text-base font-bold flex-shrink-0">
                      {currentDiyIndex + 1}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-base font-semibold mb-1">
                        {diySteps[currentDiyIndex].title}
                      </h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {diySteps[currentDiyIndex].description}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Did it work? */}
                <div>
                  <p className="text-sm font-medium mb-3">Did this step help?</p>
                  <div className="grid grid-cols-2 gap-3">
                    <Button
                      size="lg"
                      variant="outline"
                      className={cn(
                        "gap-2 border-2",
                        diyResults[currentDiyIndex] === "working" && "border-green-600 bg-green-50"
                      )}
                      onClick={() => handleDiyStepResult("working")}
                    >
                      <Check className={cn(
                        "h-5 w-5",
                        diyResults[currentDiyIndex] === "working" ? "text-green-600" : "text-muted-foreground"
                      )} />
                      <span>{currentDiyIndex < diySteps.length - 1 ? 'Yes, next step' : 'Issue resolved'}</span>
                    </Button>
                    <Button
                      size="lg"
                      variant="outline"
                      className={cn(
                        "gap-2 border-2",
                        diyResults[currentDiyIndex] === "not-working" && "border-red-600 bg-red-50"
                      )}
                      onClick={() => handleDiyStepResult("not-working")}
                    >
                      <XCircle className={cn(
                        "h-5 w-5",
                        diyResults[currentDiyIndex] === "not-working" ? "text-red-600" : "text-muted-foreground"
                      )} />
                      <span>Not working</span>
                    </Button>
                  </div>
                </div>

                {/* Completed Steps - Only Working Items */}
                {Object.entries(diyResults).some(([, result]) => result === "working") && (
                  <div className="space-y-2 animate-fade-in-up">
                    {Object.entries(diyResults)
                      .filter(([_, result]) => result === "working")
                      .map(([idx]) => (
                        <div key={idx} className="flex items-center gap-2.5 text-sm py-1.5 px-3 rounded-lg bg-muted/30 border border-border/50">
                          <div className="h-4 w-4 rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center flex-shrink-0">
                            <Check className="h-2.5 w-2.5 text-primary" />
                          </div>
                          <span className="text-muted-foreground">{diySteps[Number.parseInt(idx, 10)].title}</span>
                        </div>
                      ))}
                  </div>
                )}

                {/* Navigation Buttons */}
                <div className="flex gap-3">
                  {currentDiyIndex > 0 && (
                    <Button
                      size="lg"
                      variant="outline"
                      className="gap-2"
                      onClick={() => setCurrentDiyIndex(currentDiyIndex - 1)}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </Button>
                  )}
                  <Button
                    size="lg"
                    className="flex-1 gap-2"
                    disabled={diyResults[currentDiyIndex] === undefined}
                    onClick={handleDiyNext}
                  >
                    {currentDiyIndex < diySteps.length - 1 ? (
                      <>
                        Next Step
                        <ChevronRight className="h-4 w-4" />
                      </>
                    ) : (
                      <>
                        Complete
                        <Check className="h-4 w-4" />
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* ΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉ */}
          {/* STEP 4 ΓÇö Parts Needed                          */}
          {/* ΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉ */}
          {!loading && step === "parts" && (
            <div className="animate-fade-in-up">
              <PageHeader title="Parts Needed" subtitle="Select parts to order based on AI analysis" />
              <AIBadge />

              <div className="px-6 pb-6 space-y-6">
                {/* Filter */}
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">{selectedParts.size}/{parts.length} parts selected</span>
                  <div className="flex gap-3">
                    <button onClick={() => setSelectedParts(new Set(parts.map(p => p.id)))} className="text-xs font-medium hover:text-foreground text-muted-foreground transition-colors">Select All</button>
                    <button onClick={() => setSelectedParts(new Set())} className="text-xs font-medium hover:text-foreground text-muted-foreground transition-colors">Clear</button>
                  </div>
                </div>

                {/* Parts */}
                <div className="space-y-2">
                  {parts.map((part, idx) => (
                    <button
                      key={part.id}
                      onClick={() => togglePart(part.id)}
                      style={{ animationDelay: `${idx * 0.06}s` }}
                      className={cn(
                        "animate-fade-in-up w-full flex items-center justify-between px-4 py-3.5 rounded-xl border transition-all duration-200 text-left",
                        selectedParts.has(part.id)
                          ? "border-primary bg-primary/5"
                          : "border-border hover:border-primary/50"
                      )}
                    >
                      <div className="flex items-center gap-3">
                        <div className={cn(
                          "h-5 w-5 rounded border flex items-center justify-center flex-shrink-0 transition-colors",
                          selectedParts.has(part.id) ? "bg-primary border-primary" : "border-input"
                        )}>
                          {selectedParts.has(part.id) && <Check className="h-3 w-3 text-primary-foreground" />}
                        </div>
                        <div>
                          <p className="text-sm font-medium">{part.name}</p>
                          <p className="text-[11px] text-muted-foreground">{part.description}</p>
                        </div>
                      </div>
                      <span className="text-sm font-semibold">${part.price}</span>
                    </button>
                  ))}
                </div>

                {/* Total */}
                {selectedParts.size > 0 && (
                  <div className="flex items-center justify-between pt-3 border-t">
                    <span className="text-sm text-muted-foreground">Subtotal</span>
                    <span className="text-lg font-semibold">${partsTotal.toFixed(2)}</span>
                  </div>
                )}

                {/* Actions */}
                <div className="space-y-2">
                  <Button size="lg" className="w-full gap-2" disabled={selectedParts.size === 0} onClick={() => handlePartsAction("both")}>
                    <Wrench className="h-4 w-4" />
                    Book Technician + Order Parts
                  </Button>
                  <div className="grid grid-cols-2 gap-2">
                    <Button size="lg" variant="outline" className="gap-2" disabled={selectedParts.size === 0} onClick={() => handlePartsAction("parts")}>
                      <Package className="h-4 w-4" />
                      Order Parts
                    </Button>
                    <Button size="lg" variant="outline" className="gap-2" onClick={() => handlePartsAction("tech")}>
                      <User className="h-4 w-4" />
                      Technician Only
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉ */}
          {/* STEP 5 ΓÇö Delivery Address                      */}
          {/* ΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉ */}
          {!loading && step === "address" && (
            <div className="animate-fade-in-up">
              <PageHeader title="Delivery Address" subtitle="Where should we deliver the parts?" />

              <div className="px-6 pb-6 space-y-6">
                {/* Service address card */}
                <div className="rounded-xl border p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-start gap-3 flex-1">
                      <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm font-medium">Service Address</p>
                        {editingAddress ? (
                          <div className="space-y-3 mt-3">
                            <GoogleAddressAutocomplete
                              value={editedAddress?.address || ''} 
                              onChange={(value) => setEditedAddress(prev => prev ? { ...prev, address: value } : null)}
                              onPlaceSelected={(parsed) => setEditedAddress((prev) => prev ? {
                                ...prev,
                                address: parsed.streetAddress || prev.address,
                                city: parsed.city || prev.city,
                                state: parsed.state || prev.state,
                                zip: parsed.zipCode || prev.zip,
                                country: parsed.country || prev.country,
                              } : null)}
                              placeholder="Street Address"
                              className="w-full px-3 py-2 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary" 
                            />
                            <div className="grid grid-cols-2 gap-2">
                              <input 
                                type="text" 
                                value={editedAddress?.city || ''} 
                                onChange={(e) => setEditedAddress(prev => prev ? { ...prev, city: e.target.value } : null)}
                                placeholder="City"
                                className="w-full px-3 py-2 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary" 
                              />
                              <input 
                                type="text" 
                                value={editedAddress?.state || ''} 
                                onChange={(e) => setEditedAddress(prev => prev ? { ...prev, state: e.target.value } : null)}
                                placeholder="State"
                                className="w-full px-3 py-2 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary" 
                              />
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <input 
                                type="text" 
                                value={editedAddress?.zip || ''} 
                                onChange={(e) => setEditedAddress(prev => prev ? { ...prev, zip: e.target.value } : null)}
                                placeholder="ZIP Code"
                                className="w-full px-3 py-2 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary" 
                              />
                              <select 
                                value={editedAddress?.country || 'United States'}
                                onChange={(e) => setEditedAddress(prev => prev ? { ...prev, country: e.target.value } : null)}
                                className="w-full px-3 py-2 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary" 
                              >
                                <option value="United States">United States</option>
                                <option value="Canada">Canada</option>
                                <option value="United Kingdom">United Kingdom</option>
                                <option value="Australia">Australia</option>
                                <option value="Germany">Germany</option>
                                <option value="France">France</option>
                                <option value="India">India</option>
                                <option value="Japan">Japan</option>
                                <option value="Other">Other</option>
                              </select>
                            </div>
                          </div>
                        ) : (
                          <>
                            <p className="text-sm text-muted-foreground mt-0.5">{editedAddress?.address || currentProperty?.address || '123 Main Street'}</p>
                            <p className="text-sm text-muted-foreground">{editedAddress?.city || currentProperty?.city}, {editedAddress?.state || currentProperty?.state} {editedAddress?.zip || currentProperty?.zip}</p>
                            <p className="text-sm text-muted-foreground">{editedAddress?.country || currentProperty?.country || 'United States'}</p>
                          </>
                        )}
                      </div>
                    </div>
                    <button 
                      onClick={handleEditAddress} 
                      className="text-xs font-medium text-primary hover:text-primary/80 transition-colors ml-2"
                    >
                      {editingAddress ? 'Save' : 'Edit'}
                    </button>
                  </div>
                </div>

                {/* Home selector */}
                <div>
                  <div className="mb-2">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                      <Home className="h-3 w-3" />
                      {properties.length > 1 ? 'Switch to Different Property?' : 'Current Property'}
                    </p>
                    {properties.length > 1 && (
                      <p className="text-xs text-muted-foreground mt-1">
                        You have {properties.length} properties. Choose delivery location:
                      </p>
                    )}
                  </div>
                    
                    {selectingHome ? (
                      <div className="space-y-2 animate-fade-in-up">
                        {properties.map((property) => (
                          <button 
                            key={property.id}
                            onClick={() => {
                              switchProperty(property.id);
                              setSelectingHome(false);
                            }}
                            className={cn(
                              "w-full rounded-xl border p-3 flex items-center gap-3 text-left transition-all duration-200",
                              currentProperty?.id === property.id 
                                ? "border-primary bg-primary/5" 
                                : "hover:border-primary/50"
                            )}
                          >
                            <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                              <Home className="h-5 w-5 text-muted-foreground" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium">{property.name}</p>
                              <p className="text-xs text-muted-foreground">{property.address}, {property.city}, {property.state}</p>
                            </div>
                            {currentProperty?.id === property.id && (
                              <Check className="h-5 w-5 text-primary flex-shrink-0" />
                            )}
                          </button>
                        ))}
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => setSelectingHome(false)}
                          className="w-full"
                        >
                          Cancel
                        </Button>
                      </div>
                    ) : (
                      <button 
                        onClick={handleSwitchHome} 
                        className="w-full rounded-xl border p-4 flex items-center gap-3 text-left hover:border-primary/50 transition-all duration-200"
                      >
                        <div className="h-10 w-10 rounded-xl bg-muted flex items-center justify-center flex-shrink-0">
                          <Home className="h-5 w-5 text-muted-foreground" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium">{currentProperty?.name || 'Current Home'}</p>
                          <p className="text-xs text-muted-foreground">{currentProperty?.address}, {currentProperty?.city}, {currentProperty?.state}</p>
                        </div>
                        <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      </button>
                    )}
                </div>

                <div className="flex gap-3">
                  <Button size="lg" variant="outline" className="flex-1" onClick={onClose}>
                    Cancel
                  </Button>
                  <Button size="lg" className="flex-1" onClick={handleAddressContinue}>
                    Continue
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* ΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉ */}
          {/* STEP 6 ΓÇö Select Technician                     */}
          {/* ΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉ */}
          {!loading && step === "technician" && (
            <div className="animate-fade-in-up">
              <PageHeader title="Select Technician" subtitle="Choose a certified professional" />

              <div className="px-6 pb-6 space-y-6">
                {/* Parts ETA notice */}
                {selectedParts.size > 0 && (
                  <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-muted text-xs text-muted-foreground">
                    <Package className="h-3.5 w-3.5 flex-shrink-0" />
                    Parts arriving ~{partsEta}
                  </div>
                )}

                {/* Tech selection */}
                {selectedTech ? (
                  <>
                    {/* Selected tech card */}
                    <div className="rounded-xl border border-primary bg-primary/5 p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="h-11 w-11 rounded-full bg-muted flex items-center justify-center text-lg">{selectedTech.avatar}</div>
                          <div>
                            <p className="text-sm font-medium">{selectedTech.name}</p>
                            <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                              <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
                              {selectedTech.rating} &middot; {selectedTech.experience} experience
                            </div>
                          </div>
                        </div>
                        <button onClick={() => { setSelectedTech(null); setSelectedSlot(null); }} className="text-xs font-medium text-muted-foreground hover:text-foreground transition-colors">Change</button>
                      </div>
                      <div className="flex items-center justify-between border-t mt-3 pt-3 text-sm">
                        <span className="text-muted-foreground">Base fee</span>
                        <span className="font-semibold">${selectedTech.baseFee}</span>
                      </div>
                    </div>

                    {/* Calendar + Time slots */}
                    <CalendarView
                      timeSlots={timeSlots}
                      selectedSlot={selectedSlot}
                      onSelectSlot={setSelectedSlot}
                      partsEta={selectedParts.size > 0 ? partsEta : undefined}
                    />

                    <Button size="lg" className="w-full" disabled={!selectedSlot} onClick={handleTechContinue}>
                      Continue
                    </Button>
                  </>
                ) : (
                  <>
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Available technicians</p>
                    <div className="space-y-2">
                      {MOCK_TECHNICIANS.map((tech, idx) => (
                        <button
                          key={tech.id}
                          onClick={() => setSelectedTech(tech)}
                          style={{ animationDelay: `${idx * 0.06}s` }}
                          className="animate-fade-in-up w-full flex items-center justify-between px-4 py-4 rounded-xl border text-left hover:border-primary/50 hover:bg-primary/[0.03] transition-all duration-200"
                        >
                          <div className="flex items-center gap-3">
                            <div className="h-11 w-11 rounded-full bg-muted flex items-center justify-center text-lg">{tech.avatar}</div>
                            <div>
                              <p className="text-sm font-medium">{tech.name}</p>
                              <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                                <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
                                {tech.rating} &middot; {tech.experience} exp
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-semibold">${tech.baseFee}</p>
                            <p className="text-[10px] text-muted-foreground">base fee</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* ΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉ */}
          {/* STEP 7 ΓÇö Payment                               */}
          {/* ΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉ */}
          {!loading && step === "payment" && (
            <div className="animate-fade-in-up">
              <PageHeader title="Payment" subtitle="Review and complete your order" />

              <div className="px-6 pb-6 space-y-6">
                {/* Order items */}
                {selectedParts.size > 0 && (
                  <div className="space-y-3">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Order items</p>
                    {parts.filter(p => selectedParts.has(p.id)).map(part => (
                      <div key={part.id} className="flex items-center justify-between py-2.5 border-b last:border-b-0">
                        <div className="flex items-center gap-3">
                          <div className="h-9 w-9 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                            <Package className="h-4 w-4 text-muted-foreground" />
                          </div>
                          <div>
                            <p className="text-sm font-medium">{part.name}</p>
                            <p className="text-[11px] text-muted-foreground">{part.description}</p>
                          </div>
                        </div>
                        <span className="text-sm font-medium">${part.price.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Payment method */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Payment method</p>
                    <button onClick={handleAddCard} className="text-xs font-medium text-primary hover:text-primary/80 transition-colors">+ Add Card</button>
                  </div>
                  
                  {/* Add new card form */}
                  {addingCard && (
                    <div className="mb-4 p-4 rounded-xl border bg-muted/50 space-y-3 animate-fade-in-up">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-semibold">Add New Card</p>
                        <button onClick={() => setAddingCard(false)} className="text-xs text-muted-foreground hover:text-foreground">
                          Cancel
                        </button>
                      </div>
                      
                      <div>
                        <input 
                          type="text" 
                          placeholder="Card Number"
                          value={newCard.cardNumber.replaceAll(/(.{4})/g, '$1 ').trim()}
                          onChange={(e) => {
                            const value = e.target.value.replaceAll(/\D/g, '').slice(0, 16);
                            setNewCard({ ...newCard, cardNumber: value });
                            if (cardErrors.cardNumber) setCardErrors({ ...cardErrors, cardNumber: undefined });
                          }}
                          className={cn(
                            "w-full px-3 py-2 rounded-lg border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary",
                            cardErrors.cardNumber ? "border-destructive" : "border-input"
                          )}
                        />
                        {cardErrors.cardNumber && (
                          <p className="text-xs text-destructive mt-1">{cardErrors.cardNumber}</p>
                        )}
                      </div>
                      
                      <div>
                        <input 
                          type="text" 
                          placeholder="Cardholder Name"
                          value={newCard.cardholderName}
                          onChange={(e) => {
                            setNewCard({ ...newCard, cardholderName: e.target.value });
                            if (cardErrors.cardholderName) setCardErrors({ ...cardErrors, cardholderName: undefined });
                          }}
                          className={cn(
                            "w-full px-3 py-2 rounded-lg border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary",
                            cardErrors.cardholderName ? "border-destructive" : "border-input"
                          )}
                        />
                        {cardErrors.cardholderName && (
                          <p className="text-xs text-destructive mt-1">{cardErrors.cardholderName}</p>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <input 
                            type="text" 
                            placeholder="MM"
                            value={newCard.expiryMonth}
                            onChange={(e) => {
                              const value = e.target.value.replaceAll(/\D/g, '').slice(0, 2);
                              if (value === '' || (Number.parseInt(value, 10) >= 0 && Number.parseInt(value, 10) <= 12)) {
                                setNewCard({ ...newCard, expiryMonth: value });
                                if (cardErrors.expiryMonth) setCardErrors({ ...cardErrors, expiryMonth: undefined });
                              }
                            }}
                            className={cn(
                              "w-full px-3 py-2 rounded-lg border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary",
                              cardErrors.expiryMonth ? "border-destructive" : "border-input"
                            )}
                          />
                          {cardErrors.expiryMonth && (
                            <p className="text-xs text-destructive mt-1">{cardErrors.expiryMonth}</p>
                          )}
                        </div>
                        <div>
                          <input 
                            type="text" 
                            placeholder="YY"
                            value={newCard.expiryYear}
                            onChange={(e) => {
                              const value = e.target.value.replaceAll(/\D/g, '').slice(0, 2);
                              setNewCard({ ...newCard, expiryYear: value });
                              if (cardErrors.expiryYear) setCardErrors({ ...cardErrors, expiryYear: undefined });
                            }}
                            className={cn(
                              "w-full px-3 py-2 rounded-lg border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary",
                              cardErrors.expiryYear ? "border-destructive" : "border-input"
                            )}
                          />
                          {cardErrors.expiryYear && (
                            <p className="text-xs text-destructive mt-1">{cardErrors.expiryYear}</p>
                          )}
                        </div>
                        <div>
                          <input 
                            type="text" 
                            placeholder="CVV"
                            value={newCard.cvv}
                            onChange={(e) => {
                              const value = e.target.value.replaceAll(/\D/g, '').slice(0, 4);
                              setNewCard({ ...newCard, cvv: value });
                              if (cardErrors.cvv) setCardErrors({ ...cardErrors, cvv: undefined });
                            }}
                            className={cn(
                              "w-full px-3 py-2 rounded-lg border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary",
                              cardErrors.cvv ? "border-destructive" : "border-input"
                            )}
                          />
                          {cardErrors.cvv && (
                            <p className="text-xs text-destructive mt-1">{cardErrors.cvv}</p>
                          )}
                        </div>
                      </div>
                      
                      <Button onClick={handleSaveNewCard} size="sm" className="w-full">
                        Save Card
                      </Button>
                    </div>
                  )}
                  
                  {/* All saved cards - always visible */}
                  <div className="space-y-2">
                    {paymentCards.map((card) => (
                      <button
                        key={card.id}
                        onClick={() => setSelectedCard(card.id)}
                        className={cn(
                          "w-full rounded-xl border p-4 flex items-center justify-between text-left transition-all duration-200",
                          selectedCard === card.id 
                            ? "border-primary bg-primary/5" 
                            : "border-border hover:border-primary/50"
                        )}
                      >
                        <div className="flex items-center gap-3 flex-1">
                          <div className="h-8 w-12 rounded bg-muted border border-border flex items-center justify-center">
                            <CreditCard className="h-4 w-4 text-muted-foreground" />
                          </div>
                          <div>
                            <p className="text-sm font-medium">{card.brand.toUpperCase()} &bull;&bull;&bull;&bull; {card.last4}</p>
                            <p className="text-[11px] text-muted-foreground">Expires {card.expMonth}/{String(card.expYear).slice(-2)}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {selectedCard === card.id && (
                            <div className="flex items-center gap-1 text-primary">
                              <Check className="h-4 w-4" />
                              <span className="text-xs font-medium">Selected</span>
                            </div>
                          )}
                          {paymentCards.length > 1 && (
                            <button 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteCard(card.id);
                              }}
                              className="text-xs text-destructive hover:text-destructive/80 px-2 py-1 rounded border border-destructive/20"
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* No card warning - show when user has zero saved cards and not adding one */}{paymentCards.length === 0 && !addingCard && (<div className="flex items-center justify-between rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm"><span className="text-amber-800">Add a payment method to continue</span><button onClick={handleAddCard} className="text-amber-800 font-medium underline">Add Card</button></div>)}{/* Policy */}
                <button onClick={() => setAcceptedPolicy(!acceptedPolicy)} className="flex items-start gap-3 text-left w-full py-2">
                  <div className={cn(
                    "h-5 w-5 rounded border flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors",
                    acceptedPolicy ? "bg-primary border-primary" : "border-input"
                  )}>
                    {acceptedPolicy && <Check className="h-3 w-3 text-primary-foreground" />}
                  </div>
                  <div>
                    <p className="text-sm font-medium">I accept the cancellation policy</p>
                    <p className="text-[11px] text-muted-foreground leading-relaxed">
                      Free cancellation up to 4 hours before the scheduled time. Late cancellations may incur a fee.
                    </p>
                  </div>
                </button>

                {/* Cost Summary - Simplified */}
                <div className="rounded-xl border bg-muted/30 p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-base font-semibold">Order Summary</h3>
                  </div>
                  
                  {/* Main Items */}
                  <div className="space-y-2.5">
                    {selectedParts.size > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Parts & Shipping ({selectedParts.size} items)</span>
                        <span className="font-medium">${(partsTotal + shipping).toFixed(2)}</span>
                      </div>
                    )}
                    
                    {bookingMode !== "parts" && selectedTech && (
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Service Visit</span>
                        <span className="font-medium">${laborCost.toFixed(2)}</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Collapsible Details */}
                  {showCostDetails && (
                    <div className="space-y-2 pt-2 border-t animate-fade-in-up">
                      {selectedParts.size > 0 && (
                        <>
                          <div className="flex justify-between text-xs">
                            <span className="text-muted-foreground pl-4">Parts subtotal</span>
                            <span>${partsTotal.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between text-xs">
                            <span className="text-muted-foreground pl-4">Shipping</span>
                            <span>${shipping.toFixed(2)}</span>
                          </div>
                        </>
                      )}
                      <div className="flex justify-between text-xs">
                        <span className="text-muted-foreground pl-4">Tax (8%)</span>
                        <span>${tax.toFixed(2)}</span>
                      </div>
                      {bookingMode !== "parts" && selectedTech && (
                        <div className="flex justify-between text-xs">
                          <span className="text-muted-foreground pl-4">Labor ({estimatedHours}h @ ${hourlyRate.toFixed(0)}/h)</span>
                          <span>${laborCost.toFixed(2)}</span>
                        </div>
                      )}
                      {selectedParts.size > 0 && (
                        <div className="flex justify-between text-xs">
                          <span className="text-muted-foreground pl-4">Est. Delivery</span>
                          <span>{partsEta}</span>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Show/Hide Details Button */}
                  <button 
                    onClick={() => setShowCostDetails(!showCostDetails)}
                    className="w-full text-xs text-primary hover:text-primary/80 font-medium py-1 flex items-center justify-center gap-1 transition-colors"
                  >
                    {showCostDetails ? 'Hide Details' : 'Show Details'}
                  </button>
                  
                  {/* Total */}
                  <div className="flex justify-between text-base font-bold pt-3 border-t">
                    <span>Total Due Today</span>
                    <span className="text-lg text-primary">${grandTotal.toFixed(2)}</span>
                  </div>
                  
                  <p className="text-xs text-muted-foreground">
                    Final cost may vary based on actual work required
                  </p>
                </div>
                
                {/* Error Display */}
                {apiError && (
                  <div className="rounded-xl border border-destructive bg-destructive/10 p-4">
                    <div className="flex items-center gap-2 text-destructive">
                      <AlertTriangle className="h-4 w-4" />
                      <span className="text-sm font-medium">{apiError}</span>
                    </div>
                  </div>
                )}

                <Button size="lg" className="w-full" disabled={!acceptedPolicy || !selectedCard} onClick={handlePlaceOrder}>
                  Place Order
                </Button>
              </div>
            </div>
          )}

          {/* ΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉ */}
          {/* STEP 8 ΓÇö Confirmation                          */}
          {/* ΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉ */}
          {!loading && step === "confirmation" && (
            <div className="animate-fade-in-up">
              <div className="px-6 pt-8 pb-6 space-y-6">
                {/* Success icon */}
                <div className="text-center">
                  <div className="inline-flex h-16 w-16 rounded-full bg-primary/10 items-center justify-center mb-4 animate-scale-in">
                    <Check className="h-8 w-8 text-primary" />
                  </div>
                  <h2 className="text-2xl font-semibold tracking-tight">Order Confirmed!</h2>
                  <p className="text-sm text-muted-foreground mt-1">We&apos;ll send you updates on your order</p>
                </div>

                {/* IDs */}
                <div className="space-y-2">
                  {selectedParts.size > 0 && (
                    <div className="flex items-center justify-between px-4 py-3 rounded-xl border border-primary/20 bg-primary/5 animate-fade-in-up" style={{ animationDelay: "0.1s" }}>
                      <div>
                        <p className="text-[11px] text-muted-foreground">Order ID</p>
                        <p className="text-sm font-semibold">{orderId}</p>
                      </div>
                      <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-primary/10 text-primary">Confirmed</span>
                    </div>
                  )}
                  {bookingMode !== "parts" && (
                    <div className="flex items-center justify-between px-4 py-3 rounded-xl border border-primary/20 bg-primary/5 animate-fade-in-up" style={{ animationDelay: "0.15s" }}>
                      <div>
                        <p className="text-[11px] text-muted-foreground">Booking ID</p>
                        <p className="text-sm font-semibold">{bookingId}</p>
                      </div>
                      <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-primary/10 text-primary">Confirmed</span>
                    </div>
                  )}
                </div>

                {/* Summary */}
                <div className="rounded-xl border p-4 space-y-4">
                  {selectedParts.size > 0 && (
                    <div className="flex items-center gap-3 text-sm">
                      <Package className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                      <div><p className="text-muted-foreground">Parts Ordered</p><p className="font-medium">{selectedParts.size} items</p></div>
                    </div>
                  )}
                  {selectedParts.size > 0 && (
                    <div className="flex items-center gap-3 text-sm">
                      <Calendar className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                      <div><p className="text-muted-foreground">Expected Delivery</p><p className="font-medium">{partsEta}</p></div>
                    </div>
                  )}
                  {bookingMode !== "parts" && selectedTech && (
                    <div className="flex items-center gap-3 text-sm">
                      <User className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                      <div><p className="text-muted-foreground">Technician</p><p className="font-medium">{selectedTech.name}</p></div>
                    </div>
                  )}
                  {selectedSlot && (
                    <div className="flex items-center gap-3 text-sm">
                      <Clock className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                      <div><p className="text-muted-foreground">Appointment</p><p className="font-medium">{selectedSlot.date}, {selectedSlot.time}</p></div>
                    </div>
                  )}
                  <div className="flex items-center gap-3 text-sm pt-3 border-t">
                    <CreditCard className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                    <div><p className="text-muted-foreground">Total Charged</p><p className="font-semibold">${grandTotal.toFixed(2)}</p></div>
                  </div>
                </div>

                <Button size="lg" className="w-full" onClick={onClose}>
                  Done
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
